import { getMedianPrices } from '@lib/api-services/suburbProfileService'
import { useState, useEffect } from 'react'

/**
 * Custom hook to fetch median prices for a suburb with optional filters
 * @param {string} slug - Suburb slug
 * @param {string} type - Type filter ('house', 'unit', or '')
 * @param {string} bedrooms - Bedrooms filter ('Overall', '1', '2', '3', '4', '5+')
 * @param {Object} initialData - Initial median prices data from server-side props
 * @returns {Object} { medianPrices, isLoading, error }
 */
function useMedianPrices(
  slug,
  type = 'all',
  bedrooms = 'Overall',
  initialData = null
) {
  const [medianPrices, setMedianPrices] = useState(initialData)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!slug) {
      setMedianPrices(initialData)
      return
    }

    const hasFilters = type || bedrooms
    if (!hasFilters) {
      setMedianPrices(initialData)
      return
    }

    const fetchFilteredData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const params = {}
        if (type) params.type = type
        if (bedrooms) params.bedrooms = bedrooms

        const response = await getMedianPrices(slug, params)
        setMedianPrices(response)
      } catch (err) {
        console.error('Error fetching filtered median prices:', err)
        setError(err)
        if (initialData) {
          setMedianPrices(initialData)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchFilteredData()
  }, [type, bedrooms, slug, initialData])

  useEffect(() => {
    const hasFilters = type || bedrooms
    if (initialData && !hasFilters) {
      setMedianPrices(initialData)
    }
  }, [initialData, type, bedrooms])

  return { medianPrices, isLoading, error }
}

export default useMedianPrices
